<template>
  <div class="jira-cloud-step-1">
    <div class="row mb-3">
      <div class="col-12">
        <h2>Configuration</h2>
      </div>
      <div class="col-12">
        <p>Please choose one method</p>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <div class="config-card">
          <div class="config-header">
            <div class="header-title">
              <h2>One-way</h2>
            </div>
          </div>
          <div class="config-option"
               :class="{ selected: selectedOption === IMPORT_OPTION_ONLY_IMPORT }"
               @click="selectOption(IMPORT_OPTION_ONLY_IMPORT)">
            <div class="option-content">
              <RadioElement v-model="selectedOption" name="syncOption"
                            :value="IMPORT_OPTION_ONLY_IMPORT"
                            id="onlyImport"/>
              <div class="option-details">
                <h3>Only Import</h3>
                <ul>
                  <li><span><LucideIcon icon="Check"/></span> Import Existing Issues from Jira
                  </li>
                  <li><span><LucideIcon icon="Check"/></span> Manually Pull New Issues from Jira
                  </li>
                </ul>
              </div>
            </div>
            <div class="option-label">
              <img class="metastory-logo light" src="@/assets/logo/logo-mark.svg" title="metastory"
                   style="height: 16px;"/>
              <img class="metastory-logo dark" src="@/assets/logo/logo-mark-white.svg" title="metastory"
                   style="height: 16px;"/>
              <LucideIcon icon="ArrowLeft"/>
              <img src="@/assets/integrations/jira.svg" title="jira"
                   style="height: 16px;"/>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <div :class="['config-card', { upgrade: hasFreeProduct }]">
          <div class="config-header">
            <div class="header-title">
              <h2>Two-way</h2>
              <UpgradeElement v-if="hasFreeProduct" size="sm"/>
            </div>
            <ButtonElement v-if="hasFreeProduct" class="btn--ms btn--accent btn--md"
                           @click="goToUpgradePage">
              <LucideIcon icon="ChevronsUp"/>
              Upgrade Now
            </ButtonElement>
          </div>

          <div class="config-option"
               :class="{ selected: selectedOption === IMPORT_OPTION_MANUAL_SYNC, disabled: hasFreeProduct }"
               @click="selectOption(IMPORT_OPTION_MANUAL_SYNC)">
            <div class="option-content">
              <RadioElement v-model="selectedOption"
                            name="syncOption"
                            :value="IMPORT_OPTION_MANUAL_SYNC"
                            id="manualSync"
                            :disabled="hasFreeProduct"/>
              <div class="option-details">
                <h3>Manual Sync</h3>
                <ul>
                  <li><span><LucideIcon icon="Check"/></span> Manually Push Updated Features to Jira
                  </li>
                  <li><span><LucideIcon icon="Check"/></span> Manually Pull New Issues from Jira
                  </li>
                </ul>
              </div>
            </div>
            <div class="option-label">
              <img class="metastory-logo light" src="@/assets/logo/logo-mark.svg" title="metastory"
                   style="height: 16px;"/>
              <img class="metastory-logo dark" src="@/assets/logo/logo-mark-white.svg" title="metastory"
                   style="height: 16px;"/>
              <LucideIcon icon="ArrowRightLeft"/>
              <img src="@/assets/integrations/jira.svg" title="jira"
                   style="height: 16px;"/>
            </div>
          </div>

          <div class="config-option"
               :class="{ selected: selectedOption === IMPORT_OPTION_AUTO_SYNC, disabled: hasFreeProduct }"
               @click="selectOption(IMPORT_OPTION_AUTO_SYNC)">
            <div class="option-content">
              <RadioElement v-model="selectedOption"
                            name="syncOption"
                            :value="IMPORT_OPTION_AUTO_SYNC"
                            id="autoSync"
                            :disabled="hasFreeProduct"/>
              <div class="option-details">
                <h3>Auto Sync</h3>
                <ul>
                  <li><span><LucideIcon icon="Check"/></span> Auto-sync updates between Features and Jira
                  </li>
                  <li><span><LucideIcon icon="Check"/></span> Full bidirectional sync for Features and Jira Issues
                  </li>
                </ul>
              </div>
            </div>
            <div class="option-label">
              <img class="metastory-logo light"  src="@/assets/logo/logo-mark.svg" title="metastory"
                   style="height: 16px;"/>
              <img class="metastory-logo dark"  src="@/assets/logo/logo-mark-white.svg" title="metastory"
                   style="height: 16px;"/>
              <LucideIcon icon="RotateCw"/>
              <img src="@/assets/integrations/jira.svg" title="jira"
                   style="height: 16px;"/>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <div class="config-footer">
          <div class="radio-group">
            <RadioElement v-model="extraOption"
                          name="extraOption"
                          :value="EXTRA_OPTION_IMPORT_ALL"
                          id="importAllIssues"
                          label="Import All Issues"
            />

            <RadioElement v-model="extraOption"
                          name="extraOption"
                          :value="EXTRA_OPTION_IGNORE_COMPLETED"
                          id="ignoreCompletedIssues"
                          label="Ignore Completed Issues"
                          data-tooltip-position="top"
                          data-tooltip="Issues marked as 'done' will not be imported from Jira"
            />

            <RadioElement v-show="selectedOption !== IMPORT_OPTION_ONLY_IMPORT"
                          v-model="extraOption"
                          name="extraOption"
                          :value="EXTRA_OPTION_IGNORE_EXISTING"
                          id="ignoreExistingIssues"
                          label="Ignore All Existing Issues"
                          data-tooltip-position="top"
                          data-tooltip="Only new features from metastory will create new issues in Jira and be synced"
            />
          </div>
          <div class="button-group">
            <ButtonElement class="btn--ms btn--silent-tertiary btn--regular" @click="$emit('cancelClick')">Cancel</ButtonElement>
            <ButtonElement class="btn--ms btn--accent" @click="clickContinue()">Continue
            </ButtonElement>
          </div>
        </div>
      </div>
    </div>
    <upgrade-popup v-if="showUpgradePopup" @close="upgradePopupStore.closePopup"/>
  </div>
</template>

<script lang="ts">
import {defineComponent} from 'vue';
import RadioElement from '@shared/components/elements/radio/radio.element.vue';
import UpgradeElement from "@shared/components/elements/labels/upgrade.element.vue";
import ButtonElement from "@shared/components/elements/button/button.element.vue";
import {useWorkspaceStore} from "@shared/stores/workspace.store";
import UpgradePopup from "@shared/components/widgets/upgrade-popup.vue";
import {useUpgradePopupStore} from "@domains/boards/stores/help.upgrade.store";
import {SettingNs} from "@shared/types";

export default defineComponent({
  name: 'JiraCloudStep1',
  components: {
    UpgradePopup,
    ButtonElement,
    UpgradeElement,
    RadioElement
  },
  emits: ['cancelClick', 'continueClick'],
  setup() {
    const workspaceStore = useWorkspaceStore();
    const upgradePopupStore = useUpgradePopupStore();
    return {workspaceStore, upgradePopupStore};
  },
  data() {
    return {
      IMPORT_OPTION_ONLY_IMPORT: 'ONLY_IMPORT',
      IMPORT_OPTION_MANUAL_SYNC: 'MANUAL_SYNC',
      IMPORT_OPTION_AUTO_SYNC: 'AUTO_SYNC',

      EXTRA_OPTION_IMPORT_ALL: 'IMPORT_ALL',
      EXTRA_OPTION_IGNORE_COMPLETED: 'IGNORE_COMPLETED',
      EXTRA_OPTION_IGNORE_EXISTING: 'IGNORE_EXISTING',

      selectedOption: 'ONLY_IMPORT',
      extraOption: 'IMPORT_ALL'
    };
  },
  computed: {
    hasFreeProduct(): boolean {
      return this.workspaceStore.hasFreeProduct;
    },
    showUpgradePopup(): boolean {
      return this.upgradePopupStore.isPopupOpen;
    }
  },
  watch: {
    selectedOption: {
      handler(newValue) {
        if (newValue === this.IMPORT_OPTION_ONLY_IMPORT) {
          this.extraOption = this.EXTRA_OPTION_IMPORT_ALL;
        }
      }
    }
  },
  methods: {
    selectOption(option: string) {
      if (!this.hasFreeProduct || option === this.IMPORT_OPTION_ONLY_IMPORT) {
        this.selectedOption = option;
      }
    },
    clickContinue() {
      if (!this.selectedOption) return;
      this.$emit('continueClick', {
        selectedOption: this.selectedOption,
        extraOption: this.extraOption
      });
    },
    goToUpgradePage() {
      this.$router.push({ name: SettingNs.SettingRouteEnums.UPGRADE });
    }
  }
});
</script>

<style lang="scss" scoped>
.config-card {
  background-color: var(--card-bg);
  border: 1px solid var(--card-border);
  box-shadow: var(--card-shadow);
  display: flex;
  flex-direction: column;
  gap: var(--space-16);
  padding: var(--space-16);
  border-radius: $br-md;
  margin-bottom: var(--space-16);

  &.upgrade {
    background: var(--orange-linear);
  }

  h2 {
    font-size: var(--fs-16);
    margin: var(--space-0);
    padding: var(--space-0);
  }
}

.config-header {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .header-title {
    display: flex;
    align-items: center;
    gap: var(--space-8);
    min-height: 34px;
  }
}

.config-option {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: var(--space-16);
  background-color: var(--light-100);
  border: 1px solid var(--border-neutral);
  border-radius: $br-lg;
  cursor: pointer;
  gap: var(--space-16);

  &:hover {
    box-shadow: var(--shadow-outline);

    :deep(.form-check-input) {
      box-shadow: var(--shadow-outline) !important;
    }
  }

  &.selected {
    box-shadow: var(--shadow-outline);
  }

  &.disabled {
    box-shadow: none;
    cursor: default;

    :deep(.form-check-input) {
      box-shadow: none !important;
    }
    .option-details{
      h3{
        color: var(--text-neutral);
      }
    }
  }

  .option-content {
    display: flex;
    align-items: center;
    gap: var(--space-16);

    .option-details {
      display: flex;
      flex-direction: column;
      gap: var(--space-8);

      h3 {
        font-size: var(--fs-16);
        margin: var(--space-0);
      }

      ul {
        list-style-type: none;
        padding: var(--space-0);
        margin: var(--space-0);
        font-size: var(--fs-14);
        color: var(--text-neutral);

        span {
          margin-right: var(--space-8);
        }
      }
    }
  }

  .option-label {
    display: flex;
    gap: var(--space-8);
    color: var(--text-neutral);
  }
}


</style>
